export type FAQTopic =
  | 'customer-support'
  | 'sales-objection'
  | 'onboarding'
  | 'security'
  | 'pricing'
  | 'setup'
  | 'privacy'
  | 'trial'
  | 'features';

export type FAQPlacement = 'Home' | 'Pricing' | 'SafetyPolicy';

export interface FAQItem {
  id: string;
  question: string;
  answer: string;
  topics: FAQTopic[];
  placements: FAQPlacement[];
  intentStage: 'Awareness' | 'Consideration' | 'Decision' | 'Trust';
  stakeholderSource: string;
}

export const faqs: FAQItem[] = [
  {
    id: 'support-alert-frequency',
    question: 'How often will Joey send me safety alerts about my child\'s messages?',
    answer:
      "Joey provides AI Smart Alert everytime your child's data is scanned. Apple prevents realtime reading of messages, which is a plus and minus. The plus is that we can scan all of your child's message history, making Joey the only platform to be able to do so. In a very very small number of cases, real-time alerts are required.",
    topics: ['customer-support', 'features'],
    placements: ['Home', 'SafetyPolicy'],
    intentStage: 'Awareness',
    stakeholderSource: 'Support Lead Maya (Customer Support)',
  },
  {
    id: 'support-device-compatibility',
    question: 'Does Joey work if my child uses both an iPhone and an iPad?',
    answer:
      "Support has confirmed that Joey pairs with your child\'s Apple iPad and iPhone, so message monitoring and web search insights follow them across iPhone and iPad devices.",
    topics: ['customer-support', 'features'],
    placements: ['Home'],
    intentStage: 'Awareness',
    stakeholderSource: 'Support Lead Maya (Customer Support)',
  },
  {
    id: 'support-privacy-guardrails',
    question: 'Can I keep Joey from reading every conversation my child has?',
    answer:
      "Joey\'s privacy guardrails mean Joey's AI focuses on risky patterns instead of everyday chats. Legal confirmed the privacy controls align with COPPA and state surveillance laws, and everything is logged for parent consent.",
    topics: ['customer-support', 'privacy', 'security'],
    placements: ['Home', 'SafetyPolicy'],
    intentStage: 'Trust',
    stakeholderSource: 'Privacy Counsel Alex (Legal)',
  },
  {
    id: 'support-alert-context',
    question: 'Will Joey show me the full conversation that triggered an alert?',
    answer:
      "Yes—Joey captures the surrounding messages so you can understand context before responding. If the content is extremely sensitive, the system redacts personal identifiers while preserving enough detail to act, a compromise Brand approved for tone and empathy.",
    topics: ['customer-support', 'security', 'privacy'],
    placements: ['SafetyPolicy'],
    intentStage: 'Trust',
    stakeholderSource: 'Support Lead Maya (Customer Support)',
  },
  {
    id: 'sales-data-security',
    question: 'How is Joey keeping our family\'s data secure?',
    answer:
      "CISO emphasized that Joey\'s security program is built around ISO/IEC 27001:2022, with BSI conducting annual surveillance audits. All family data is encrypted in transit and at rest, stored on AWS, and access is limited through role-based controls and audit logging.",
    topics: ['sales-objection', 'security', 'privacy'],
    placements: ['SafetyPolicy', 'Pricing', 'Home'],
    intentStage: 'Trust',
    stakeholderSource: 'CISO Jordan (Security Stakeholder)',
  },
  {
    id: 'security-iso-verification',
    question: 'How can I verify Joey\'s ISO/IEC 27001:2022 certification?',
    answer:
      "Parents can confirm Laneway Analytics\' certificate number IS 728964 by visiting the BSI client directory and searching for Joey or Laneway Analytics, or by using the direct listing at https://www.bsigroup.com/en-GB/products-and-services/assessment-and-certification/validation-and-verification/client-directory-profile/LANEWA-0047771987-000. The Joey Security & Trust hub (joeyfamily.com/security) walks through these verification steps with screenshots you can share with schools or co-guardians, and links to Laneway's latest ISO 27001 recertification announcement for added assurance.",
    topics: ['security', 'privacy'],
    placements: ['Home', 'SafetyPolicy', 'Pricing'],
    intentStage: 'Trust',
    stakeholderSource: 'CISO Jordan (Security Stakeholder)',
  },
  {
    id: 'security-audit-cadence',
    question: 'How often is Joey\'s security reviewed by third parties?',
    answer:
      "Laneway Analytics completes at least one independent penetration test every year or after major releases, and BSI performs annual ISO/IEC 27001 surveillance audits to verify that controls stay effective.",
    topics: ['security'],
    placements: ['SafetyPolicy', 'Pricing'],
    intentStage: 'Trust',
    stakeholderSource: 'CISO Jordan (Security Stakeholder)',
  },
  {
    id: 'security-data-residency',
    question: 'Where is my child\'s Joey data stored?',
    answer:
      "All Joey data is hosted on Amazon Web Services, keeping it close to our core families while meeting regional privacy expectations. Backups and monitoring stay within the same region so information never the AWS data centres.",
    topics: ['security', 'privacy'],
    placements: ['Home', 'SafetyPolicy', 'Pricing'],
    intentStage: 'Trust',
    stakeholderSource: 'Privacy Counsel Alex (Legal)',
  },
  {
    id: 'security-gdpr-compliance',
    question: 'Is Joey compliant with GDPR for families in the EU or UK?',
    answer:
      "Yes. Legal confirmed Joey meets GDPR requirements through documented lawful bases for processing, parental consent workflows, data minimisation, and rapid export/delete requests. ISO/IEC 27001 controls and AWS hosting support these commitments.",
    topics: ['security', 'privacy'],
    placements: ['Home', 'SafetyPolicy', 'Pricing'],
    intentStage: 'Trust',
    stakeholderSource: 'Privacy Counsel Alex (Legal)',
  },
  {
    id: 'sales-parental-control-comparison',
    question: 'What makes Joey different from other parental control apps?',
    answer:
      "Sales Lead Priya pointed out that Joey focuses on predictive AI safety—not screen-time policing. Unique differentiators include iMessage-native scanning, tone-aware bullying detection, and zero-config onboarding. These advantages are featured in demos and case studies.",
    topics: ['sales-objection', 'features'],
    placements: ['Home', 'Pricing'],
    intentStage: 'Awareness',
    stakeholderSource: 'Sales Lead Priya (Revenue)',
  },
  {
    id: 'sales-cost-justification',
    question: 'Why is Joey priced higher than basic monitoring tools?',
    answer:
      "Pricing Strategy notes that Joey is cheaper than most 3rd party parenting applications like Questodio. And Joey is the only ethically designed monitoring service that is not spyware and actually works, unlike Bark which almost never connect to kid's devices and almost all alerts are false alerts.",
    topics: ['sales-objection', 'pricing'],
    placements: ['Pricing'],
    intentStage: 'Consideration',
    stakeholderSource: 'Pricing Manager Elena (Revenue)',
  },
  {
    id: 'sales-trial-to-paid',
    question: 'Can I test Joey before committing to a paid subscription?',
    answer:
      "Yes, Brand approved messaging that Joey Basic is free with no credit card required. During onboarding, Joey highlights a countdown banner and suggests safety checklists so families can experience premium alerts before upgrading.",
    topics: ['sales-objection', 'trial', 'onboarding'],
    placements: ['Home', 'Pricing'],
    intentStage: 'Consideration',
    stakeholderSource: 'Sales Lead Priya (Revenue)',
  },
  {
    id: 'onboarding-setup-time',
    question: 'How long does it take to connect my child\'s devices to Joey?',
    answer:
      "Implementation Specialist Marco reported that most families complete setup in under 10 minutes. The onboarding wizard steps parents through three easy steps utilising native Apple encryption and functions - no spyware or jail-breaking.",
    topics: ['onboarding', 'setup'],
    placements: ['Home', 'Pricing'],
    intentStage: 'Consideration',
    stakeholderSource: 'Implementation Specialist Marco (Onboarding)',
  },
  {
    id: 'onboarding-shared-guardians',
    question: 'Can multiple caregivers manage the same Joey account?',
    answer:
      "Yes—Family Admins can invite co-guardians with role-based access so each caregiver receives relevant alerts. Brand asked that we emphasize cooperative language in the UI to reinforce trust between parents and teens.",
    topics: ['onboarding', 'customer-support'],
    placements: ['Home', 'SafetyPolicy'],
    intentStage: 'Decision',
    stakeholderSource: 'Customer Success Dana (Onboarding)',
  },
  {
    id: 'onboarding-school-compliance',
    question: 'Is Joey compliant with my child\'s school device policies?',
    answer:
      "Legal confirmed Joey operates within FERPA guidelines because monitoring happens on family-owned backups, not school-managed hardware. We provide a letter of attestation parents can share with school administrators during device check-ins.",
    topics: ['onboarding', 'privacy', 'security'],
    placements: ['SafetyPolicy', 'Pricing'],
    intentStage: 'Trust',
    stakeholderSource: 'Privacy Counsel Alex (Legal)',
  },
  {
    id: 'sales-data-retention',
    question: 'How long does Joey store our data, and can we delete it?',
    answer:
      "Data retention defaults to 90 days for analytics, after which alerts are anonymized. Parents can trigger immediate deletion from the dashboard, and Legal approved the plain-language explanation we surface in consent dialogs.",
    topics: ['sales-objection', 'privacy'],
    placements: ['SafetyPolicy'],
    intentStage: 'Trust',
    stakeholderSource: 'Privacy Counsel Alex (Legal)',
  },
  {
    id: 'onboarding-child-notifications',
    question: 'Will my child know Joey is monitoring their messages?',
    answer:
      "Brand and Legal aligned on transparent communication: we strongly advise parents talk to their children and let them know Joey is running to protect them and their families like anti-virus. Through Joey, parents can extend more trust to their children rather than blindly blocking apps.",
    topics: ['onboarding', 'privacy', 'customer-support'],
    placements: ['Home', 'SafetyPolicy'],
    intentStage: 'Awareness',
    stakeholderSource: 'Brand Director Lila (Brand)',
  },
  {
    id: 'onboarding-child-notifications',
    question: 'Do you sell or share my child’s data with advertisers?',
    answer:
      "No. Joey never sells your family’s data or shares it with advertisers. Data is used only to provide and improve Joey’s safety features.",
    topics: ['onboarding', 'privacy', 'customer-support'],
    placements: ['Home', 'SafetyPolicy'],
    intentStage: 'Awareness',
    stakeholderSource: 'Brand Director Lila (Brand)',
  },
  {
    id: 'onboarding-child-notifications',
    question: 'Can I delete my data if we stop using Joey?',
    answer:
      "Yes. You can request deletion from within your account. We’ll remove your Joey data from our active systems and backups following our retention policy, and you can safely uninstall Joey Desktop.",
    topics: ['onboarding', 'privacy', 'customer-support'],
    placements: ['Home', 'SafetyPolicy'],
    intentStage: 'Awareness',
    stakeholderSource: 'Brand Director Lila (Brand)',
  }
]

export default faqs
